import { formatFc, formatForeignMap } from '../../utils/fcDocuments';
import FcOpenItemsTable from '../common/FcOpenItemsTable';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Button, CircularProgress, Paper, Tab, Table, TableBody, TableCell, TableContainer,
  TableFooter, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { FileDownload as FileDownloadIcon, Print as PrintIcon } from '@mui/icons-material';
import { SupplierReportService } from '../../services/supplierReport.service';
import { SupplierService } from '../../services/supplier.service';
import { ApAgingReport, SupplierStatement } from '../../interfaces/procurement/supplierReport.interface';
import { Supplier } from '../../interfaces/procurement/supplier.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { downloadCsv } from '../../utils/csv';
import SearchableSelect from '../common/SearchableSelect';
import { formatMoney } from '../../utils/format';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';

/**
 * Payables reporting: the supplier aging report (what we owe, by days past due) and a
 * per-supplier statement (bills and payments with a running balance owed).
 */
const SupplierPayablesReport: React.FC = () => {
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('supplierId') ? 1 : 0);

  const [aging, setAging] = useState<ApAgingReport | null>(null);
  const [agingLoading, setAgingLoading] = useState(true);

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierId, setSupplierId] = useState<string>(searchParams.get('supplierId') ?? '');
  const [from, setFrom] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return toLocalIsoDate(d);
  });
  const [to, setTo] = useState<string>(() => toLocalIsoDate(new Date()));
  const [statement, setStatement] = useState<SupplierStatement | null>(null);
  const anyFc = !!statement?.rows.some(r => !!r.currencyCode);   // B4-polish3
  const [statementLoading, setStatementLoading] = useState(false);

  useEffect(() => {
    const institutionId = AuthService.getSelectedInstitution();
    SupplierReportService.aging(institutionId)
      .then(setAging)
      .catch(e => toast.showApiError(e, 'Failed to load supplier aging report'))
      .finally(() => setAgingLoading(false));
    // The supplier master list needs SUPPLIER_MANAGE, which a bills-only viewer (e.g. an
    // accountant) lacks; the picker then falls back to the suppliers named in the aging report.
    SupplierService.listByInstitution(0, 200, 'ALL')
      .then(page => setSuppliers(page.content))
      .catch(e => {
        if (e?.response?.status !== 403) toast.showApiError(e, 'Failed to load suppliers');
      });
    const initialSupplier = searchParams.get('supplierId');
    if (initialSupplier) void runStatement(initialSupplier);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const supplierOptions = React.useMemo(() => {
    const byId = new Map<string, { id: string; name: string }>();
    suppliers.forEach(s => byId.set(s.id, { id: s.id, name: s.name }));
    aging?.rows.forEach(r => { if (!byId.has(r.supplierId)) byId.set(r.supplierId, { id: r.supplierId, name: r.supplierName }); });
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [suppliers, aging]);

  const runStatement = async (id: string = supplierId) => {
    if (!id) return;
    setStatement(null);
    try {
      setStatementLoading(true);
      setStatement(await SupplierReportService.statement(id, from, to));
    } catch (e) {
      toast.showApiError(e, 'Failed to load supplier statement');
    } finally {
      setStatementLoading(false);
    }
  };

  const exportAging = () => {
    if (!aging) return;
    const headers = ['Supplier', 'Current', '1-30', '31-60', '61-90', '90+', 'Total', 'Unapplied debit-note credit'];
    const rows = aging.rows.map(r => [
      r.supplierName, r.bucketCurrent, r.bucket1To30, r.bucket31To60, r.bucket61To90, r.bucket90Plus, r.total,
      r.unappliedCredit ?? 0,
    ]);
    downloadCsv('supplier-aging.csv', headers, [
      ...rows,
      ['TOTALS', aging.grandCurrent, aging.grand1To30, aging.grand31To60, aging.grand61To90, aging.grand90Plus, aging.grandTotal, aging.grandUnappliedCredit ?? 0],
    ]);
  };

  const exportStatement = () => {
    if (!statement) return;
    const headers = ['Date', 'Type', 'Document #', 'Reference', 'Debit', 'Credit', 'Balance owed'];
    const rows = statement.rows.map(r => [
      formatDate(r.date), r.documentType, r.documentNumber, r.reference ?? '',
      r.debit > 0 ? r.debit.toFixed(2) : '', r.credit > 0 ? r.credit.toFixed(2) : '', r.balance.toFixed(2),
    ]);
    const safeName = statement.supplierName.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'supplier';
    downloadCsv(`supplier-statement-${safeName}-${toLocalIsoDate(new Date())}.csv`, headers, [
      ['', 'Opening balance', '', '', '', '', statement.openingBalance.toFixed(2)],
      ...rows,
      ['', 'Closing balance', '', '', '', '', statement.closingBalance.toFixed(2)],
    ]);
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="procurement.payables.page">
      <Typography variant="h5" gutterBottom>Supplier Payables</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Aging" />
        <Tab label="Statement" />
      </Tabs>

      {tab === 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {aging ? `Outstanding supplier bills as of ${formatDate(aging.asOfDate)}` : ' '}
            </Typography>
            <Button data-help="procurement.payables.exportAging" variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportAging} disabled={!aging || agingLoading}>
              Export CSV
            </Button>
          </Box>
          {agingLoading ? <CircularProgress /> : aging && (
            <TableContainer component={Paper}>
              <Table size="small" data-help-section="procurement.payables.aging">
                <TableHead>
                  <TableRow>
                    <TableCell data-help="procurement.payables.age.supplier">Supplier</TableCell>
                    <TableCell data-help="procurement.payables.age.current" align="right">Current</TableCell>
                    <TableCell data-help="procurement.payables.age.d30" align="right">1–30</TableCell>
                    <TableCell data-help="procurement.payables.age.d60" align="right">31–60</TableCell>
                    <TableCell data-help="procurement.payables.age.d90" align="right">61–90</TableCell>
                    <TableCell data-help="procurement.payables.age.d90plus" align="right">90+</TableCell>
                    <TableCell data-help="procurement.payables.age.total" align="right">Total</TableCell>
                    <TableCell data-help="procurement.payables.age.credit" align="right">Unapplied debit-note credit</TableCell>
                    {aging.rows.some(r => formatForeignMap(r.foreignOutstanding)) && <TableCell data-help="procurement.payables.age.fc" align="right">Open in foreign currency</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {aging.rows.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center">No outstanding supplier bills.</TableCell></TableRow>
                  ) : aging.rows.map(r => (
                    <TableRow
                      key={r.supplierId}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => { setSupplierId(r.supplierId); setTab(1); void runStatement(r.supplierId); }}
                    >
                      <TableCell>{r.supplierName}</TableCell>
                      <TableCell align="right">{formatMoney(r.bucketCurrent)}</TableCell>
                      <TableCell align="right">{formatMoney(r.bucket1To30)}</TableCell>
                      <TableCell align="right">{formatMoney(r.bucket31To60)}</TableCell>
                      <TableCell align="right">{formatMoney(r.bucket61To90)}</TableCell>
                      <TableCell align="right">{formatMoney(r.bucket90Plus)}</TableCell>
                      <TableCell align="right">{formatMoney(r.total)}</TableCell>
                      <TableCell align="right">{formatMoney(r.unappliedCredit ?? 0)}</TableCell>
                      {aging.rows.some(x => formatForeignMap(x.foreignOutstanding)) && <TableCell align="right">{formatForeignMap(r.foreignOutstanding)}</TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow sx={{ '& td': { fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' } }}>
                    <TableCell>TOTALS</TableCell>
                    <TableCell align="right">{formatMoney(aging.grandCurrent)}</TableCell>
                    <TableCell align="right">{formatMoney(aging.grand1To30)}</TableCell>
                    <TableCell align="right">{formatMoney(aging.grand31To60)}</TableCell>
                    <TableCell align="right">{formatMoney(aging.grand61To90)}</TableCell>
                    <TableCell align="right">{formatMoney(aging.grand90Plus)}</TableCell>
                    <TableCell align="right">{formatMoney(aging.grandTotal)}</TableCell>
                    <TableCell align="right">{formatMoney(aging.grandUnappliedCredit ?? 0)}</TableCell>
                    {aging.rows.some(x => formatForeignMap(x.foreignOutstanding)) && <TableCell />}
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {tab === 1 && (
        <>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }} data-help-section="procurement.payables.filter">
            <SearchableSelect
              data-help="procurement.payables.field.supplier"
              label="Supplier"
              size="small"
              sx={{ flex: '1 1 260px', maxWidth: 360 }}
              options={supplierOptions}
              value={supplierId}
              onChange={id => { setSupplierId(id); setStatement(null); }}
              getOptionLabel={s => s.name}
            />
            <TextField data-help="procurement.payables.field.from" label="From" type="date" size="small" value={from}
              sx={{ flex: '1 1 160px', maxWidth: 200 }}
              onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField data-help="procurement.payables.field.to" label="To" type="date" size="small" value={to}
              sx={{ flex: '1 1 160px', maxWidth: 200 }}
              onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
            <Button data-help="procurement.payables.run" variant="contained" onClick={() => runStatement()} disabled={!supplierId || statementLoading}>
              {statementLoading ? <CircularProgress size={20} color="inherit" /> : 'Run'}
            </Button>
          </Box>

          {statement && (
            <Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button data-help="procurement.payables.exportStatement" variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportStatement}>Export CSV</Button>
                <Button data-help="procurement.payables.print" variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>Print</Button>
              </Box>
              <Paper sx={{ p: 3 }} className="printable-area">
                <Typography variant="h6">{statement.supplierName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Period: {formatDate(statement.fromDate)} – {formatDate(statement.toDate)}
                </Typography>
                <Box sx={{ display: 'flex', gap: 4, mt: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Typography variant="body2"><strong>Opening balance owed:</strong> {formatMoney(statement.openingBalance)}</Typography>
                  <Typography variant="body2"><strong>Closing balance owed:</strong> {formatMoney(statement.closingBalance)}</Typography>
                  {(statement.unappliedAdvances ?? 0) > 0 && (
                    <>
                      <Typography variant="body2"><strong>Unapplied advances:</strong> {formatMoney(statement.unappliedAdvances ?? 0)}</Typography>
                      <Typography variant="body2"><strong>Net owed after advances:</strong> {formatMoney(statement.netBalance ?? statement.closingBalance)}</Typography>
                    </>
                  )}
                </Box>
                <TableContainer>
                  <Table size="small" data-help-section="procurement.payables.statement">
                    <TableHead>
                      <TableRow>
                        <TableCell data-help="procurement.payables.st.date">Date</TableCell>
                        <TableCell data-help="procurement.payables.st.type">Type</TableCell>
                        <TableCell data-help="procurement.payables.st.doc">Document #</TableCell>
                        <TableCell data-help="procurement.payables.st.ref">Reference</TableCell>
                        <TableCell data-help="procurement.payables.st.debit" align="right">Debit (paid)</TableCell>
                        <TableCell data-help="procurement.payables.st.credit" align="right">Credit (billed)</TableCell>
                        <TableCell data-help="procurement.payables.st.balance" align="right">Balance owed</TableCell>
                        {anyFc && <TableCell data-help="procurement.payables.st.fc" align="right">Foreign amount</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {statement.rows.length === 0 ? (
                        <TableRow><TableCell colSpan={anyFc ? 8 : 7} align="center">No transactions in this period.</TableCell></TableRow>
                      ) : statement.rows.map((r, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>{formatDate(r.date)}</TableCell>
                          <TableCell>{r.documentType}</TableCell>
                          <TableCell>{r.documentNumber}</TableCell>
                          <TableCell>{r.reference ?? ''}</TableCell>
                          <TableCell align="right">{r.debit > 0 ? formatMoney(r.debit) : ''}</TableCell>
                          <TableCell align="right">{r.credit > 0 ? formatMoney(r.credit) : ''}</TableCell>
                          <TableCell align="right">{formatMoney(r.balance)}</TableCell>
                          {anyFc && (
                            <TableCell align="right">
                              {r.currencyCode && r.foreignAmount != null ? formatFc(r.currencyCode, r.foreignAmount) : ''}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <FcOpenItemsTable items={statement.foreignOpenItems} />
              </Paper>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default SupplierPayablesReport;
